<script setup>
// The expanded view of the template the operator picked, sitting above the grid.
//
// Two panes because they answer different questions: the artwork at full width answers
// "is this the right creative?", and the rendered bubble answers "does the copy read
// right with the variables still unfilled?". The old 320px side rail could only do the
// second, and badly.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TemplatePreview from './TemplatePreview.vue';

const props = defineProps({
  template: { type: Object, required: true },
  agentName: { type: String, default: '' },
  agentAvatar: { type: String, default: '' },
});

defineEmits(['clear']);

const { t } = useI18n();

const statusTone = computed(() => {
  switch (props.template.status) {
    case 'APPROVED':
      return 'text-n-teal-11 bg-n-teal-3';
    case 'REJECTED':
      return 'text-n-ruby-11 bg-n-ruby-3';
    default:
      return 'text-n-amber-11 bg-n-amber-2';
  }
});
</script>

<template>
  <section
    class="overflow-hidden border rounded-xl border-n-brand bg-n-alpha-1"
  >
    <header
      class="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-n-weak"
    >
      <i class="i-lucide-check-circle-2 size-4 shrink-0 text-n-brand" />
      <h3 class="text-sm font-medium break-words text-n-slate-12">
        {{ template.name }}
      </h3>

      <span
        v-if="template.status"
        class="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded-full"
        :class="statusTone"
      >
        {{ template.status }}
      </span>
      <span
        v-if="template.category"
        class="text-[10px] uppercase tracking-wide text-n-slate-10"
      >
        {{ template.category }}
      </span>
      <span
        v-if="template.language"
        class="px-1.5 py-0.5 text-[10px] rounded bg-n-alpha-2 text-n-slate-10"
      >
        {{ template.language }}
      </span>

      <button
        type="button"
        class="inline-flex items-center gap-1 ml-auto text-xs text-n-slate-10 hover:text-n-slate-12"
        @click="$emit('clear')"
      >
        <i class="i-lucide-x size-3.5" />
        {{ t('OUTBOUNDS.TEMPLATE.CLEAR') }}
      </button>
    </header>

    <!-- One pane, not two. The artwork is the top of the message, so it is shown where
         it lands — inside the bubble — and the whole conversation scrolls. -->
    <div class="flex justify-center p-4">
      <div class="w-full max-w-md">
        <p class="mb-2 text-xs font-medium text-center text-n-slate-11">
          {{ t('OUTBOUNDS.TEMPLATE.PREVIEW_TITLE') }}
        </p>
        <TemplatePreview
          :template="template"
          :agent-name="agentName"
          :agent-avatar="agentAvatar"
        />
      </div>
    </div>
  </section>
</template>
